import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

import { useWindowSize } from "../../../../hooks/useWindowResize";
import { device, size } from "../../../../constants";
import { SlideBulletNavigation } from "../../../blog/RelatedPosts/SlideBulletNavigation";
import { HighlightBar } from "../../HighlightBar";

const Section = styled.section`
  background: ${({ theme }) => theme.colors["neutral-07"]};
  .bar > div {
    margin: 0 auto;
  }
`;

const Title = styled.h2`
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors["neutral-01"]};
  margin: 16px 0;

  @media ${device.laptopL} {
    font-size: 32px;
    line-height: 48px;
    margin-top: 24px;
  }
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 18px;
  line-height: 32px;
  letter-spacing: 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors["neutral-01"]};

  @media ${device.laptopL} {
    font-size: 20px;
    line-height: 36px;
  }
`;

const LogosWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-top: 40px;

  @media ${device.laptopL} {
    margin-top: 56px;
    gap: 56px;
  }
`;

const Logo = styled.img`
  height: 64px;
  max-width: 175px;
  width: 100%;
  margin: 0 auto;
  display: block;
  object-fit: contain;

  @media ${device.laptopL} {
    width: auto;
    height: auto;
    object-fit: none;
    max-height: 64px;
    max-width: 243px;
  }
`;

const SwiperWrapper = styled.div`
  margin-top: 40px;

  .bullets-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 40px;
  }

  .bullet {
    background-color: var(--product-color);
    opacity: 0.5;
  }
  
  .bullet-active {
    opacity: 1;
  }

  :is([data-theme="dark"] &) {
    .bullet {
      background-color: var(--product-color);
    }
  }
`;

const tools = [
	{
		alt: "Automation Anywhere",
		src: "/landings/assets/images/products/rpa/logo-automation-anywhere-rpa.png",
	},
	{
		alt: "Blue Prism",
		src: "/landings/assets/images/products/rpa/logo-blueprism-rpa.svg",
	},
	{
		alt: "UiPath",
		src: "/landings/assets/images/products/rpa/logo-uipath-rpa.png",
	},
	{
		alt: "WDG Automation",
		src: "/landings/assets/images/products/rpa/logo-wdg-automation-rpa.png",
	},
	{
		alt: "Microsoft Power Automate",
		src: "/landings/assets/images/products/rpa/logo-microsoft-power-automate-rpa.svg",
	},
];

export default function RpaToolsSection() {
	const [activeBullet, setActiveBullet] = useState(0);
	const { t } = useTranslation();
	const { width } = useWindowSize();

	return (
		<Section>
			<div className="container-blog">
				<div className="bar">
					<HighlightBar />
				</div>

				<Title>{t("products.rpa.tools.title")}</Title>
				<Description
					dangerouslySetInnerHTML={{
						__html:
							width <= size.tablet
								? t("products.rpa.tools.description.mobile")
								: t("products.rpa.tools.description.desktop"),
					}}
				/>

				{width <= size.laptopM ? (
					<SwiperWrapper>
						<Swiper
							slidesPerView={1}
							navigation={false}
							breakpoints={{
								[size.tablet]: {
									slidesPerView: 2,
								},
								[size.laptop]: {
									slidesPerView: 3,
								},
							}}
							onSlideChange={(e) => {
								setActiveBullet(e.activeIndex);
							}}
						>
							{tools.map((tool) => (
								<SwiperSlide key={tool.alt}>
									<Logo src={tool.src} alt={tool.alt} />
								</SwiperSlide>
							))}

							<SlideBulletNavigation activeBullet={activeBullet} />
						</Swiper>
					</SwiperWrapper>
				) : (
					<LogosWrapper>
						{tools.map((tool) => (
							<Logo key={tool.alt} src={tool.src} alt={tool.alt} />
						))}
					</LogosWrapper>
				)}
			</div>
		</Section>
	);
}
